import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import { Plus, Settings  } from "lucide-react";

const RoleList = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAll();
      setRoles(response.data || []);
    } catch (err) {
      Alert.error('Failed to load roles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this role? This action cannot be undone.',
      'Delete Role'
    );
    
    if (confirmed) {
      try {
        await roleService.delete(roleId);
        Alert.success('Role deleted successfully!');
        fetchRoles();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete role');
      }
    }
  };

  const toggleDropdown = (roleId) => {
    setActiveDropdown(activeDropdown === roleId ? null : roleId);
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex h-screen bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Settings className="w-5 h-5 text-tertiary" />
                      Roles
                    </h1>
                    {/*<p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage your system roles</p>*/}
                  </div>
                  <button
                    onClick={() => navigate('/roles/add')}
                    className="flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs transition-all duration-300 rounded-sm sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonhoverTxt hover:bg-buttonhoverBg font-medium whitespace-nowrap border"
                  >
                    <Plus className="w-4 h-4 text-white" />
                    Add Roles
                  </button>
                </div>

                {/* Table */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  {loading ? (
                    <div className="p-6 sm:p-8 text-center text-gray-600">
                      <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      <p className="text-sm sm:text-base">Loading roles...</p>
                    </div>
                  ) : roles.length === 0 ? (
                    <div className="p-6 sm:p-8 text-center text-sm sm:text-base text-gray-600">No roles found</div>
                  ) : (
                    <>
                      {/* Desktop Table View */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">ROLE NAME</th>
                              <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">CREATED BY</th>
                              <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">CREATED DATE</th>
                              <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">LAST UPDATED</th>
                              <th className="px-4 lg:px-6 py-3 lg:py-4 text-left text-xs lg:text-sm font-semibold">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {roles.map((role) => (
                              <tr key={role.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 lg:px-6 py-2 lg:py-2">
                                  <button
                                    onClick={() => navigate(`/roles/view/${role.id}`)}
                                    className="font-medium text-secondary hover:text-secondary-700 hover:underline text-sm lg:text-base"
                                  >
                                    {role.role_name}
                                  </button>
                                </td>
                                <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">{role.created_by || 'System'}</td>
                                <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">
                                  {new Date(role.created_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td className="px-4 lg:px-6 py-2 lg:py-2 text-gray-700 text-sm lg:text-base">
                                  {new Date(role.updated_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td className="px-4 lg:px-6 py-2 lg:py-2 relative">
                                  <button
                                    onClick={() => toggleDropdown(role.id)}
                                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                                    title="Actions"
                                  >
                                    <svg 
                                      xmlns="http://www.w3.org/2000/svg" 
                                      className="h-5 w-5 text-gray-600" 
                                      fill="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <circle cx="12" cy="5" r="2"/>
                                      <circle cx="12" cy="12" r="2"/>
                                      <circle cx="12" cy="19" r="2"/>
                                    </svg>
                                  </button>
                                  
                                  {activeDropdown === role.id && (
                                    <div 
                                      ref={dropdownRef}
                                      className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10"
                                    >
                                      <button
                                        onClick={() => {
                                          navigate(`/roles/view/${role.id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                                      >
                                        <span>👁️</span> View Details
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigate(`/roles/edit/${role.id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                                      >
                                        <span>✏️</span> Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleDelete(role.id);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                      >
                                        <span>🗑️</span> Delete
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden divide-y divide-gray-200">
                        {roles.map((role) => (
                          <div key={role.id} className="p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between mb-3">
                              <button
                                onClick={() => navigate(`/roles/view/${role.id}`)}
                                className="font-semibold text-orange-600 hover:text-orange-700 hover:underline text-base"
                              >
                                {role.role_name}
                              </button>
                              <button
                                onClick={() => toggleDropdown(role.id)}
                                className="p-1.5 hover:bg-gray-100 rounded-md transition-colors flex-shrink-0"
                                title="Actions"
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  className="h-5 w-5 text-gray-600" 
                                  fill="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2"/>
                                  <circle cx="12" cy="12" r="2"/>
                                  <circle cx="12" cy="19" r="2"/>
                                </svg>
                              </button>
                            </div>
                            
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Created By:</span>
                                <span className="text-gray-900 font-medium">{role.created_by || 'System'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Created:</span>
                                <span className="text-gray-900 font-medium">
                                  {new Date(role.created_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Updated:</span>
                                <span className="text-gray-900 font-medium">
                                  {new Date(role.updated_at).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>

                            {activeDropdown === role.id && (
                              <div 
                                ref={dropdownRef}
                                className="mt-3 bg-white rounded-md shadow-lg border border-gray-200"
                              >
                                <button
                                  onClick={() => {
                                    navigate(`/roles/view/${role.id}`);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors border-b border-gray-100"
                                >
                                  <span>👁️</span> View Details
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/roles/edit/${role.id}`);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-900 hover:bg-gray-50 transition-colors border-b border-gray-100"
                                >
                                  <span>✏️</span> Edit
                                </button>
                                <button
                                  onClick={() => {
                                    handleDelete(role.id);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <span>🗑️</span> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Bottom Info */}
                      <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-t">
                        <div className="text-xs sm:text-sm text-gray-700">
                          Showing {roles.length} role{roles.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleList;